import './ReportHistory.scss';
import {useEffect, useMemo, useRef, useState, ReactNode} from 'react';
import { Link, useSearchParams } from "react-router-dom";
import {useTenantApi} from '@/hooks/useTenantApi';
import {useUser} from '@/contexts/UserContext';
import {useBetterNavigate} from '@/utils';
import { AgGridReact } from 'ag-grid-react';
import { ByzzerTable } from '@/components/ByzzerTable';
import { CellEditRequestEvent, CellEvent, ColDef, FilterChangedEvent, GridReadyEvent } from 'ag-grid-community';
import { openErrorModal, confirm, alert as byzzerAlert, ByzzerModal} from '@/components/form';
import {getProductBySku} from "@/services/product.service";
import classnames from 'classnames';
import { ByzzerMenu } from '@/components/ByzzerMenu';
import { ByzzerMask } from '@/components/ByzzerMask/ByzzerMask';
import FavoriteIcon from '@/components/icons/FavoriteIcon';
import {ByzzerButton, ZonedDate } from '@byzzer/ui-components';
import { CharacteristicCondition } from '@/types/ReportRun';
import { intersection } from 'lodash';
import { formatInTimeZone } from 'date-fns-tz';
import { CompanyCreditUsage, ReportRunSummary } from '@/types/ApiTypes';
import {FLOATING_DATE_PICKER_FILTER_PARAMS} from '@/constants/table.constants'
import { reportDescriptionUrl } from '@/config/globalVars';
import ReportInformation from '../ReportInformation';
import { TipIcon } from '@/components/icons';
import { ByzColDef } from '@/types/TableTypes';
import Tippy from '@tippyjs/react';
import {Placement} from '@popperjs/core';
import { ReportValidationResultMessages } from '@/constants/report.constants';
import { openRenameReportSeriesName } from '@/components/ConfigurationEditors/ReportConfigurationEditor/ScheduleReportStep/ScheduleModal';
import { triggerToast } from '@/notifications/ByzzerToast';
import UseByzzerConditionalLink from '@/hooks/UseByzzerConditionalLink';
import { DEFAULT_BYZZER_ERROR_MODAL_SUBTITLE, DEFAULT_BYZZER_ERROR_MODAL_TITLE } from '@/components/ByzzerModal/byzzerError.constants';

/**
 * Useful Links - 
 * https://www.ag-grid.com/react-data-grid/ 
 * https://www.ag-grid.com/react-data-grid/component-cell-renderer/ 
 * https://www.ag-grid.com/react-data-grid/cell-editing/
 * https://www.ag-grid.com/react-data-grid/cell-editing-start-stop/
 * https://www.ag-grid.com/react-data-grid/videos/
 * https://www.ag-grid.com/react-data-grid/change-detection/
 */ 

const NOTES_PLACEHOLDER = 'Add notes';
const NOTES_FIELD_ID = 'notes' as const;

type ByzzerTipProps = {
    label: ReactNode;
    content: string;
    placement?: Placement;
    delay?: [number, number];
    children?: ReactNode;
    theme?: 'dark' | 'light';
}

const ByzzerTip = ({ // TODO: should ultimately utilize ByzzerTipIcon and update that to allow for content other than tip icon (renaming it, of course, to something like 'ByzzerTip').
    children,
    label = children,
    content,
    placement = 'bottom',
    delay = [200, 0],
    theme = 'dark'
}: ByzzerTipProps) => {
    return (
        <Tippy content={content} placement={placement} delay={delay} theme={theme}>
            {typeof label === 'string' ? <span>{label}</span> : <>{label}</>}
        </Tippy>
    );
};

type ReportHistoryColId = 'id' | 'reportTitle' | 'runBy' | 'runDtm' | 'seriesName' | 'brandsAndManufacturers' | 'categories' | 'timePeriod' | 'markets' | 'charDimensions' | 'characteristics' | 'isFavorite' | typeof NOTES_FIELD_ID |'attributes'|'attributeGroup';

function ReportHistory() {
    const {
        getMySubscriptionUsage,
        getCompanyReportRuns,
        deleteProductUsingActionId,
        saveFavorites,
        saveNotes,
        updateScheduledReportSeriesName,
        updateSeriesNameForReportRun,
        checkDuplicateSeries,
    } = useTenantApi();
    const baseClassName = 'report-history'; 

    const {
        user,
        categories: subscriptionCategories,
        omniCategories: subscriptionOmniCategories,
        subscription,
        subscribedSkus,
        bonusSkus,
        maxDataDates,
    } = useUser();
    const [searchParams, setSearchParams] = useSearchParams();
    const [reportRuns, setReportRuns] = useState<ReportRunSummary[]>([]);    
    const navigate = useBetterNavigate();
    const [loading, setLoading] = useState(false);    
    const [canRunReports, setCanRunReports] = useState(false);    
    const gridRef = useRef<AgGridReact>(null);
    const menuRefs = useRef<any[]>([]);     
    const [showReportInfo, setShowReportInfo] = useState(false);
    const [selectedReport, setSelectedReport] = useState({} as ReportRunSummary);
    const reportUsage = useRef<CompanyCreditUsage>({} as CompanyCreditUsage);
    const isFreeUser = useMemo(() => subscription?.metadata?.isFree === true, [subscription]);

    useEffect(() => {
        if (user?.role) {
            setCanRunReports(['user', 'admin'].includes(user?.role));
        }
    }, [user]);

    useEffect(() => {
        loadHistory();

        async function getRemainingRuns() {
            let usage = await getMySubscriptionUsage();
            reportUsage.current = usage;
        }
        getRemainingRuns();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    

    async function loadHistory() {        
        try {
            setLoading(true);
            // todo: move this outside of this function
            const companyReportRuns: ReportRunSummary[] = await getCompanyReportRuns(subscribedSkus);
            const sortedRuns = companyReportRuns.sort((a, b) => b.runDtm.localeCompare(a.runDtm));            
            setReportRuns(sortedRuns.map( (item)=>{ return {...item , runDtm: formatInTimeZone(new Date(item.runDtm), 'America/New_York', 'yyyy-MM-dd') }} ))
            
        } catch (err: any) {
            openErrorModal({
                title: DEFAULT_BYZZER_ERROR_MODAL_TITLE,
                content: (
                    <>
                        <p>{DEFAULT_BYZZER_ERROR_MODAL_SUBTITLE}</p>
                    </>
                ),
                errorId: err.id
            });
        } finally {
            setLoading(false);
        }
    };

    /**
    * handle favorite feature by clicking star icon in each record which is a toggle btn   
    */
    async function handleFavorites (favRecord:ReportRunSummary) { 
            
        const reportId: number  = favRecord.id ;
        const isfav   : boolean = !favRecord.isFavorite ;
        
        // gridRef.current?.props.rowData?.find((element) => element.id === reportId)        
        // let favindex = gridRef.current?.props.rowData?.findIndex((element) => element.id === reportId)        
        try {            
            let updatedReportRuns = gridRef.current?.props.rowData?.map( obj =>{
                if (obj.id === reportId) {
                    return {...obj, isFavorite: !obj.isFavorite}    
                } 
                return obj ;          
            })            
             setReportRuns(updatedReportRuns as ReportRunSummary[]);
            //  setLoading(true);
             await saveFavorites( reportId , isfav );
            
        } catch (error:any) {
            console.log(error.message);
        } 
    } 
   /**
    * newvalue is set as undefined if newvalue equals to oldvalue OR newvalue is empty/white spaces OR newvalue equals to Add Notes
    * save happens only if newvalue.trim() is not undefined
    */
    async function handleNotesEdit({data: { id: reportId }, oldValue, newValue}: CellEditRequestEvent<ReportRunSummary>){
        try {
            const newNotes: string = !newValue || ['', NOTES_PLACEHOLDER].includes(newValue.trim()) ? undefined : newValue.trim();
            
            if (!(Boolean(!oldValue) && Boolean(!newNotes))) { // if editing an already empty cell and enter nothing or just spaces, then do nothing
                let updatedReportRuns = gridRef.current?.props.rowData?.map( obj =>{
                    if (obj.id === reportId) {
                        return {...obj, notes: newNotes}    
                    } 
                    return obj           
                })
                
                setReportRuns(updatedReportRuns as ReportRunSummary[]);
                //  setLoading(true);
                await saveNotes( reportId , newNotes );                 
            }
        } catch (error:any) {
            console.log(error.message);
        }        
    }

    // Function to remove properties starting with 'condition', but keep 'conditions' array
    const filterJson = (json) => {
        const filtered = {};

        Object.keys(json).forEach(key => {
            const value = json[key];

            // Skip properties that start with 'condition' except 'conditions'
            if (key.startsWith('condition') && key !== 'conditions') {
                return;
            }

            if (typeof value === 'object' && value !== null) {
                // Recursively filter objects
                filtered[key] = Array.isArray(value)
                    ? value.map(item => (typeof item === 'object' && item !== null ? filterJson(item) : item))
                    : filterJson(value);
            } else {
                // Copy primitive values
                filtered[key] = value;
            }
        });

        return filtered;
    };
    
    const queryStringToJson = (filtersString: string | null): Record<string, any> => {
        if (!filtersString) {
            return {}; // Return an empty object if filtersString is null
        }
        
        try {
            const decodedString = decodeURIComponent(filtersString);
            return JSON.parse(decodedString);
        } catch (error) {
            console.error('Error parsing query string to JSON:', error);
            return {}; // Return an empty object if parsing fails
        }
    };

    const jsonToQueryString = (json: Record<string, any>): string => {
        try {
            const jsonString = JSON.stringify(json);
            return encodeURIComponent(jsonString);
        } catch (error) {
            console.error('Error converting JSON to query string:', error);
            return ''; // Return an empty string if conversion fails
        }
    };

    const handleFilterChanges = (e: FilterChangedEvent<any>) => {
        let filterModel = e.api.getFilterModel();        
        // Convert filter model to query string
        const queryString = jsonToQueryString(filterJson(filterModel));
        
        setSearchParams({ filters: queryString });
    };

    // Handle Grid Ready Event
    const handleGridReady = (e: GridReadyEvent) => {
        let queryString = searchParams.get('filters');        
        const generatedFilterModelFromParams = queryStringToJson(queryString);
        
        // Set filter model on grid
        e.api.setFilterModel(generatedFilterModelFromParams);
    };

    function handleReportInfo(run: ReportRunSummary) {
        setShowReportInfo(!showReportInfo);
        setSelectedReport(run);
    }
    
    const defaultColDef = useMemo( ()=> ({
        filter: true,   // make every column use 'text' filter by default
        sortable: true,        
        floatingFilter: true, // enable floating filters by default
        resizable:true,   //set each col resizable
        width: 170, // set every column width
        flex: 0,
        autoHeight: false,
        wrapText: false, 
        suppressMenu:true,     
        lockVisible: true 
       // suppressMovable:true, to be discussed today?
        
    }), []);

    async function runReport(reportRun: ReportRunSummary , runAgainWithLatestDate?:boolean) {
        const reportSku = reportRun?.sku;
        const reportId = reportRun?.id;
        const navigateTabIndex = reportRun?.report?.metadata?.configOptions?.length;
        
        if (user?.role && !['user', 'admin'].includes(user?.role)) { 
            return;
        }

        try {
            let usage = await getMySubscriptionUsage();
            const report = getProductBySku(reportSku);

            if (!subscribedSkus.includes(reportSku)) {
                await openErrorModal({
                    title: 'Upgrade Required',
                    content: (
                        <>
                            <p>"{report.title}" is not part of your current subscription.</p>
                            <p>Please upgrade your subscription or add a bundle to get access the report.</p>
                        </>
                    ),
                });
                return;
            }

            const {premiumReports, basicReports} = usage;
            const isPremiumReport = report?.metadata?.reportClass === 'premium';
            const hasPremiumRuns = premiumReports && premiumReports.used < premiumReports.limit;
            const hasBasicRuns = basicReports && basicReports.used < basicReports.limit;

            if ((isPremiumReport && !hasPremiumRuns) || (!isPremiumReport && !hasBasicRuns)) {
                await openErrorModal({
                    title: `No ${isPremiumReport ? 'Smart' : 'Core'} Runs Remaining`,
                    content: (
                        <>
                            <p>You have used all of your subscription's {isPremiumReport ? 'smart' : 'core'} runs.</p>
                            <p>
                                Please upgrade your subscription, add a bundle or purchase additional runs to run the
                                report.
                            </p>
                        </>
                    ),
                });
                return;
            }

            let reportRunByLatestDate :Date| undefined;
            
            //Note: as the maxdate is set with EST on the api, we first fetching the date and then convert to respective localTimeZone()
            reportRunByLatestDate = new ZonedDate(maxDataDates[reportRun.report?.metadata.dataType],'EST').translateToTimeZone(ZonedDate.getLocalTimeZoneName() ) ;

            navigate(`/dashboard/configure_report`, {
                search: {
                    sku: reportSku,
                    runType: 'subscription',
                    reportId: reportId,
                },
                ...(runAgainWithLatestDate ? {
                    state: {
                        reportRunByLatestDate,
                        navigateTabIndex: runAgainWithLatestDate ? navigateTabIndex : 0,
                        //navigateTabname: 'time_period' //TODO: this requires a fix at the component level as WithUid thorws some warnings, hence navigating by tabIndex
                    }
                } : {}),
            });
        } catch (err) {
        }
    }

    // async function scheduleReport(reportRun: ReportRunSummary) {
    //     const reportSku = reportRun?.sku;
    //     const reportId = reportRun?.id;
        
    //     if (user?.role && !['user', 'admin'].includes(user?.role)) { 
    //         return;
    //     }

    //     navigate(`/dashboard/configure_report`, {
    //         search: {
    //             sku: reportSku,
    //             runType: 'subscription',
    //             reportId: reportId,
    //         },
    //         state: {
    //             scheduleMode: 'createSchedule'
    //         }
    //     });
    // }

    async function deleteReport(reportId) {
        if (
            !(await confirm({
                // @ts-ignore
                title: 'Delete Report',
                content: <div className="byzzer-allocation-warning">Are you sure you want to delete this report?</div>,
                yesLabel: 'Yes',
                noLabel: 'No',
            }))
        ) {
            return;
        }
        setLoading(true);
        let delResponse = await deleteProductUsingActionId(reportId);
        setLoading(false);
        let alertContent = delResponse === 'Success' ? 'Report deleted successfully!' : 'Something went wrong';
        byzzerAlert({
            // @ts-ignore
            content: <div className="story_modal_alertText">{alertContent}</div>,
        });
        loadHistory();
    };

    async function onRenameSeriesClick(reportRunId: number, seriesName: string | null, seriesType: string | null, seriesId: number | null) {
        try {
            const updatedReportName = await openRenameReportSeriesName({
                reportRunId,
                seriesName,
                updateScheduledReportSeriesName,
                updateSeriesNameForReportRun,
                checkDuplicateSeries,
                isScheduledReport: seriesType === 'scheduled',
                seriesId
            });

            if (updatedReportName) {
                await loadHistory();
                triggerToast({
                    content: 'series name updated successfully',
                });
            }
        } catch (error) {
            byzzerAlert({
                type: 'error',
                title: 'Series Name Update Failed',
                content: (
                    <>
                        <p>There was an unexpected error during this series name update.</p>
                        <p>Please contact the support team for additional assistance.</p>
                    </>
                ),
            });
        }
    }

    function stringArrayToString(params: string[]): string {
        return params.join('; ');
    };

    function charDimToFeatureIdString(params: any[]) {  //  // todo: add real type for param     
        return [...new Set(params?.map((item) => item.featureId ? item.featureId : item))].join('; ')
    }

    // const marketsFormatterbyCellRenderedMethod = (params) =>{ // I may need this in future
    //     return params.join(',')
    // }

    function characteristicToString(params: CharacteristicCondition[]) {
        return params?.map((c) => `${c.characteristicDisplayValue ?? c.characteristic} ${c.condition} ${c.value.join(', ')}`).join('; ');
    }

    function handleCellClick({column, api: gridApi, node}: CellEvent<ReportRunSummary>): void {
        // Could use this to edit cells, like notes
        // if (column.getColId() === NOTES_FIELD_ID) {
        //     gridApi.startEditingCell({
        //         rowIndex: node.rowIndex!,
        //         colKey: column.getId(),
        //     });
        // }
    }

    const [columnDefs] = useState<ByzColDef<ReportRunSummary, ReportHistoryColId>[]>([
        {
            colId: 'reportTitle',
            headerName: 'Report',
            pinned: 'left',
            valueGetter: ({ data }) => {
                return `${data?.report?.title}`;
            },
            tooltipValueGetter: ({ data }) => {
                return `${data?.report?.title}`;
            },
            disableHide: true
        },
        ...(window.localStorage['show-report-id'] === 'true' ? [{ colId: 'id' as const, field: 'id' as const }] : []),
        {
            colId: 'id',
            headerName: 'Report ID',
            hide: true,
            valueGetter: ({ data }) => {
                return `${data.id}`;
            },
            tooltipValueGetter: ({ data }) => {
                return `${data.id}`;
            },
        },
        {
            colId: 'runBy',
            headerName: 'Run By',
            valueGetter: ({ data }) => {
                return `${data.runBy.firstName} ${data.runBy.lastName}`;
            },
            tooltipValueGetter: ({ data }) => {
                return `${data.runBy.firstName} ${data.runBy.lastName}`;
            },
        },
        {
          ...FLOATING_DATE_PICKER_FILTER_PARAMS,
            colId: 'runDtm',
            field: 'runDtm',
            headerName: 'Run Date',
            width: 230,
        },
        {
            colId: 'seriesName',
            headerName: 'Series Name',
            valueGetter: ({ data }) => data.seriesName,
            tooltipValueGetter: ({ data }) => data.seriesName,
        },
        {
            colId: 'brandsAndManufacturers',
            headerName: 'Brand(s) / Manufacturer(s)',
            valueGetter: ({ data }: { data: ReportRunSummary }) =>
                stringArrayToString([...data.brands, ...(data?.omniFocusProducts ?? [])]),
            tooltipValueGetter: ({ data }) => stringArrayToString([...data.brands, ...(data?.omniFocusProducts ?? [])]),
            width: 300,
        },
        {
            colId: 'categories',
            headerName: 'Category(s)',
            tooltipField: 'categories',
            valueGetter: ({ data }: { data: ReportRunSummary }) => stringArrayToString(data.categories),
            cellRenderer: ({ data }: { data: ReportRunSummary }) => {
                return (
                    <span
                        className={classnames({
                            [`${baseClassName}__warning`]: Boolean(
                                !intersection(
                                    data?.categories,
                                    Boolean(data.report?.metadata.dataType === 'omni')
                                        ? subscriptionOmniCategories
                                        : subscriptionCategories
                                )?.length
                            ),
                        })}
                    >
                        {stringArrayToString(data.categories)}
                    </span>
                );
            },
        },
        {
            colId: 'timePeriod',
            headerName: 'Time Period',
            valueGetter: ({ data }) => data.timePeriod,
            tooltipValueGetter: ({ data }) => {
                return `${data.timePeriod}`;
            },
        },
        {
            colId: 'markets',
            headerName: 'Market(s)',
            tooltipField: 'markets',
            valueGetter: ({ data }) => stringArrayToString(data.markets),
        },
        {
            colId: 'attributeGroup',
            headerName: 'Attribute Group(s)',            
            valueGetter: ({ data }) => stringArrayToString(data?.attributeGroup),
        },
        {
            colId: 'attributes',
            headerName: 'Attribute(s)',            
            valueGetter: ({ data }) => stringArrayToString(data?.attributes.map((item) => {
                if (item.includes('(Custom)')) {
                    return item.split('-code-')?.[0];
                }
                return item;
            })),
        },
        {
            colId: 'charDimensions',
            headerName: 'Characteristic Dimension(s)',
            valueGetter: ({ data }) => charDimToFeatureIdString(data.charDimension),
            tooltipValueGetter: ({ data }) => charDimToFeatureIdString(data.charDimension),
            width: 300,
        },
        {
            colId: 'characteristics',
            headerName: 'Characteristic(s)',
            valueGetter: ({ data }) => characteristicToString(data.charFilters),
            tooltipValueGetter: ({ data }) => characteristicToString(data.charFilters),
            width: 300,
        },
        {
            headerName: '',
            field: 'isFavorite',
            colId: 'isFavorite',
            cellRenderer: ({ data }: { data: ReportRunSummary }) => {
                const { id } = data;
                // Function to determine the disabled state of report rerun CTA's and tooltip message
                const getMenuItemStateAndTooltip = (): { disabled: boolean, scheduledDisabled: boolean, tooltip: string } => {
                    const disableBonusReport =
                        isFreeUser &&
                        bonusSkus.includes(data.report?.sku ?? '') &&
                        (reportUsage.current.bonusReports?.limit || 0) -
                            (reportUsage.current.bonusReports?.used || 0) ===
                            0;
                    const isDisabled = Boolean(!(data.runnable && subscription?.active) || (user?.role && !['user', 'admin'].includes(user?.role))) || disableBonusReport;
                    const isScheduledDisabled = disableBonusReport;

                    const tooltip = isDisabled 
                        ? (user?.role && !['user', 'admin'].includes(user?.role) 
                            ? ReportValidationResultMessages.unauthorized_user_role
                            : ReportValidationResultMessages.subscription_not_found)
                        : ''; // An empty string if not disabled

                    return { disabled: isDisabled, scheduledDisabled: isScheduledDisabled, tooltip };
                };

                const validateReport = (
                    reportRun: ReportRunSummary
                ): boolean => {
                    const reportSku = reportRun?.sku;

                    if (user?.role && !['user', 'admin'].includes(user?.role)) {
                        return false ;
                    }

                    try {
                        const report = getProductBySku(reportSku);

                        if (!subscribedSkus.includes(reportSku)) {
                            return false ;
                        }
                        const { premiumReports, basicReports } = reportUsage.current;
                        const isPremiumReport = report?.metadata?.reportClass === 'premium';
                        const hasPremiumRuns = premiumReports && premiumReports.used < premiumReports.limit;
                        const hasBasicRuns = basicReports && basicReports.used < basicReports.limit;

                        if ((isPremiumReport && !hasPremiumRuns) || (!isPremiumReport && !hasBasicRuns)) {
                            return false ;
                        }
                        return true ;                        
                    } catch (err) {return false ;}
                };

                const { disabled: menuItemIsDisabled, tooltip: menuItemTooltipMessage, scheduledDisabled: scheduleMenuItemDisabled } = getMenuItemStateAndTooltip();
                const isCustomTimePeriod = data?.runConfig?.timePeriod?.type !== 'relative';
                const canOpen: boolean = validateReport(data);
                const canSchedule = !(user?.role && !['user', 'admin'].includes(user?.role));

                return (
                    <div className={classnames(`${baseClassName}__actions`)}>
                        <FavoriteIcon
                            className={`${baseClassName}__favorites--icon`}
                            trackClick={`Filter by favorites`}
                            selected={data?.isFavorite}
                            onClick={() => handleFavorites(data)}
                        />
                        {/* @ts-ignore */}
                        <TipIcon
                            className={`${baseClassName}__info--icon`}
                            trackClick={`Report Info (${data?.report?.title}) clicked`}
                            onClick={() => handleReportInfo(data)}
                        />
                        <div
                            key={id}
                            className={classnames(`${baseClassName}__menu-trigger`)}
                            ref={(thisElement) => (menuRefs.current[String(id)] = { current: thisElement })}
                        />
                        <ByzzerMenu
                            className={`${baseClassName}__menu`}
                            reference={menuRefs.current[String(id)]}
                            offset={[-75, -25]}
                            triggerTarget={menuRefs.current[String(id)]?.current}
                            items={[
                                {
                                    content: <Link to= {`/report/${data?.id}`}>View report</Link>,
                                    trackClick: `Report History View (${data.report?.title}) clicked`,
                                    toolTipHelpText: 'View report',
                                },

                                {
                                    disabled: menuItemIsDisabled,
                                    content: (
                                        <>
                                            {menuItemIsDisabled ? (
                                                <ByzzerTip
                                                    label={"Run a new report"}
                                                    content={menuItemTooltipMessage}
                                                />
                                            ) : (
                                                 <>{canOpen? menuContent(data,false,'Run a new report') : <>Run a new report</>}</>
                                            )}
                                        </>
                                    ),
                                    trackClick: `Report History Run-new-report (${data?.report?.title}) clicked`,
                                    toolTipHelpText: !menuItemIsDisabled ? 'Run a new report' : '',
                                    onClick: function () {
                                        if (menuItemIsDisabled || canOpen) {
                                            return;
                                        }
                                        runReport(data);
                                    },
                                },

                                {
                                    disabled: menuItemIsDisabled,
                                    content: (
                                        <>
                                            {menuItemIsDisabled ? (
                                                <ByzzerTip
                                                    label={"Run with latest date"}
                                                    content={menuItemTooltipMessage}
                                                />
                                            ) : (
                                                <>{canOpen? menuContent(data,true,'Run with latest date') : <>Run with latest date</>}</>
                                            )}
                                        </>
                                    ),
                                    trackClick: `Report History Run-again-report-with-latestdate (${data?.report?.title}) clicked`,
                                    toolTipHelpText: !menuItemIsDisabled ? 'Run report with latest date' : '',
                                    onClick: function () {
                                        if (menuItemIsDisabled || canOpen) {
                                            return;
                                        }
                                        runReport(data, true);
                                    },
                                },

                                {
                                    showIf: subscription?.active,
                                    content: 'Delete report',
                                    trackClick: `Report History delete (${data?.report?.title}) clicked`,
                                    toolTipHelpText: 'Delete',
                                    onClick: function () {
                                        deleteReport(data.id);
                                    },
                                },
                                {
                                    disabled: false,
                                    content: 'Rename series',
                                    trackClick: `report_history_rename_schedule_(${data?.report?.title})_clicked`,
                                    toolTipHelpText: 'Rename series name',
                                    onClick: function () {
                                        if (data.runBy.id !== user?.id) {
                                            byzzerAlert({
                                                content: (
                                                    <>
                                                        <p>
                                                            You cannot edit this series name because you did not
                                                            create it.
                                                        </p>
                                                    </>
                                                ),
                                            });
                                            return;
                                        }
                                        onRenameSeriesClick(data.reportRunId!, data?.seriesName, data?.seriesType!, data?.seriesId);
                                    },
                                },
                                {
                                    disabled: scheduleMenuItemDisabled,
                                    showIf: !isCustomTimePeriod,
                                    content:  <>{!isCustomTimePeriod && canSchedule ? menuContent(data,false,'Schedule Report',true) : 'Schedule Report'}</>,
                                    trackClick: `report_history_schedule_report_(${data?.report?.title})_clicked`,
                                    toolTipHelpText: 'Schedule Report',
                                }
                            ]}
                        />
                    </div>
                );
            },
            pinned: 'right',
            resizable: false,
            lockPosition: 'right',
            sortable: false,
            width: 135,
            maxWidth: 135,
            minWidth: 135,
        },

        // APPROACH -2
        {
            colId: NOTES_FIELD_ID,
            field: NOTES_FIELD_ID, // DO NOT CHANGE; needed for editing function to work
            headerName: 'Notes',
            editable: true, // enables editing
            cellEditorPopup: true,
            cellEditor: 'agLargeTextCellEditor',
            // headerComponentParams: { menuIcon: <span className={`fa fa-external-link-alt`}></span> },
            cellEditorParams: {
                maxLength: 100,
                rows: 10,
                cols: 50,
            },
            tooltipValueGetter: ({ data }) => `Click to edit`,
            cellRenderer: (props) => (
                <span
                    className={classnames(`${baseClassName}__notes`, {
                        // could add onClick handler to enable single click edit mode, etc
                        [`${baseClassName}__notes--has-notes`]: props.data.notes,
                        [`${baseClassName}__notes--empty`]: !props.data.notes,
                    })}
                >
                    {props.data.notes ?? NOTES_PLACEHOLDER}
                </span>
            ),
            headerTooltip: 'Make your notes here',
        },
    ]);

    function menuContent(reportRun: ReportRunSummary, runAgainWithLatestDate?:boolean, displayText?: string, scheduleRun?: boolean) {
        const reportSku = reportRun?.sku;
        const reportId = reportRun?.id;
        const navigateTabIndex = reportRun?.report?.metadata?.configOptions?.length;
        
        const pathParam = `/dashboard/configure_report`;
        let searchParam: any = {
            sku: reportSku,            
            runType: 'subscription',
            reportId: reportId.toString(),
        };

        if (runAgainWithLatestDate){
            searchParam = {...searchParam, tabIndex: runAgainWithLatestDate ? navigateTabIndex : 0 , runWithLatestDate : true}
        }

        if(scheduleRun){
            searchParam = {...searchParam, scheduleMode: 'createSchedule'}
        }
        return UseByzzerConditionalLink({ path: pathParam, search: searchParam, state: {}, content: displayText });
    }

    return (
        <div
            className={classnames(baseClassName, {
                [`${baseClassName}--readonly`]: !canRunReports,
                [`${baseClassName}--runnable`]: canRunReports,
            })}
        >
            <ByzzerMask show={loading} loading={loading} />
            {Boolean(!reportRuns?.length) && !loading && reportUsage === undefined && (
                <div className={`${baseClassName}__empty-state`}>You Haven't Run Any Reports Yet.</div>
            )}

            {selectedReport?.report?.title && (
                <ByzzerModal
                    show={showReportInfo}
                    onClose={() => setShowReportInfo(!showReportInfo)}
                    heading={selectedReport?.report?.title}
                    size={'large'}
                    headerType={'normal'}
                    extraIcon={
                        canRunReports &&
                        subscribedSkus.includes(selectedReport.sku) && (
                            <ByzzerButton label={'Run Report'} onClick={() => runReport(selectedReport)} />
                        )
                    }
                >
                    <ReportInformation                        
                        sku={selectedReport.sku}
                        disabled={!canRunReports || !subscribedSkus.includes(selectedReport.sku)}
                        url={
                            selectedReport?.report?.metadata.detailsUrl ||
                            `${reportDescriptionUrl}/${selectedReport?.sku}.html`
                        }
                        runReport={() => runReport(selectedReport)}
                    />
                </ByzzerModal>
            )}

            {Boolean(reportRuns?.length) && reportUsage && (
                <ByzzerTable
                    ref={gridRef} // Ref for accessing Grid's API
                    rowData={reportRuns} // Row Data for Rows
                    columnDefs={columnDefs} // Column Defs for Columns
                    defaultColDef={defaultColDef} // Column Defs for Columns
                    readOnlyEdit={true}
                    onCellEditRequest={handleNotesEdit}
                    // onCellClick={handleCellClick}
                    //  suppressClickEdit={false}
                    singleClickEdit={true}
                    onFilterChanged={handleFilterChanges}
                    onGridReady={handleGridReady}
                    tableArea={'reportHistory'}
                    enableColumnHideShow={true}
                    enableSaveLayout={true}
                    enableSaveSort={true}
                    uncheckedColumns={['id']}
                />
            )}
        </div>
    );
}


export default ReportHistory;